import { getBackendURL } from '../api';

// Re-export so existing consumers (e.g. Dashboard.js) still work
export { getBackendURL };

// Gibt die vollständige Bild-URL zurück, wenn photoUrl gesetzt ist
// Handles signed URLs (with ?token=&expires=), bare filenames, and /uploads/ paths
export function getFullPhotoUrl(photoUrl) {
  if (!photoUrl) return '';
  if (photoUrl.startsWith('http')) return photoUrl;
  
  const backendURL = getBackendURL();
  
  if (photoUrl.startsWith('/uploads/')) {
    return `${backendURL}${photoUrl}`;
  }
  // Bare filename — add /uploads/ prefix
  return `${backendURL}/uploads/${photoUrl}`;
}

/**
 * Get a thumbnail URL. Prefers server-injected thumbUrl fields, falls back to URL derivation.
 * @param {string} url - Original image URL or server-injected thumbUrl
 * @param {number} size - Thumbnail size (64 or 256)
 * @returns {string} Thumbnail URL, or original URL if derivation not possible
 */
export function getThumbUrl(url, size = 64) {
  if (!url) return '';
  // Already a thumbnail URL
  if (url.includes('.thumb-')) return getFullPhotoUrl(url);
  return getFullPhotoUrl(url);
}

/**
 * Get thumbnail URL from an object that has server-injected thumbUrl64/thumbUrl256 fields.
 * Falls back to the original URL if thumbnail fields are not present.
 * @param {Object} item - Object with thumbUrl64/thumbUrl256 or photoUrl/url/photo/filename
 * @param {number} size - Thumbnail size (64 or 256)
 * @returns {string} Resolved thumbnail URL
 */
export function getThumbFromItem(item, size = 64) {
  if (!item) return '';
  const thumbField = `thumbUrl${size}`;
  if (item[thumbField]) return getFullPhotoUrl(item[thumbField]);
  // Fallback to original
  return getFullPhotoUrl(item.photoUrl || item.url || item.photo || item.image || item.filename || '');
}

/**
 * Get the original (non-thumbnail) URL from an item.
 * Used as fallback when thumbnail fails to load.
 */
export function getOriginalUrl(item) {
  if (!item) return '';
  return getFullPhotoUrl(item.photoUrl || item.url || item.photo || item.image || item.filename || '');
}

/**
 * onError handler for <img> tags using thumbnails.
 * Falls back to original URL on thumbnail 404.
 */
export function thumbFallback(item) {
  return (e) => {
    const original = getOriginalUrl(item);
    if (original && e.target.src !== original) {
      e.target.src = original;
    }
  };
}

// Appends a cache-buster parameter, respecting existing query params (e.g. signed tokens).
// key should be a stable value like updatedAt so the browser can actually cache.
export function withCacheBuster(url, key) {
  if (!url || !key) return url || '';
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${key}`;
}

// Transforms relative /uploads/ URLs in HTML content to absolute backend URLs
// Used for note content display where images are embedded as <img src="/uploads/...">
export function transformContentUrls(html) {
  if (!html) return html;
  const backendURL = getBackendURL();
  // Replace src="/uploads/..." with full backend URL (not already absolute)
  return html.replace(/src="\/uploads\//g, `src="${backendURL}/uploads/`);
}
